import { getActiveToken } from '~/helpers/authToken';
import { UserInvoice, UserInvoiceCredit, UserInvoiceItem, UserInvoiceSetting } from './types';

const round = (val: number) => Math.round(val * 100) / 100;

export const calculatePrice = (
    [items, credits, taxes]: [UserInvoiceItem[], UserInvoiceCredit[], number],
    invoice?: UserInvoice,
    setting?: UserInvoiceSetting | null
): {
    taxes: string;
    total: string;
    before_taxes: string;
    credits_total: string;
    total_with_credits: string;
} => {
    const gross_value = invoice?.gross_value || setting?.gross_value;

    const totalWithoutCredits: number =
        items?.reduce((value, item) => value + Number(item.price_per_item) * Number(item.amount), 0) || 0;

    const totalOfCredits = credits?.reduce((sum, credit) => {
        if (credit.percentage) {
            return sum + totalWithoutCredits * (Number(credit.amount) / 100);
        } else {
            return sum + Number(credit.amount);
        }
    }, 0);

    const total: number = totalWithoutCredits - (totalOfCredits || 0);
    const itemCreditPercent = (totalOfCredits ?? 0) / totalWithoutCredits;

    const totalWithoutTaxes = items?.reduce((value, item) => {
        const sum = Number(item.price_per_item) * Number(item.amount) * (1 - itemCreditPercent);
        const itemTaxesValue =
            item.taxes === null || item.taxes === undefined || (item.taxes as unknown as string) === ''
                ? taxes
                : item.taxes;
        const itemTaxes = Number(itemTaxesValue) / 100;

        if (gross_value) return value + sum / (1 + itemTaxes);

        return value + sum * (1 + itemTaxes);
    }, 0);

    const totalOfTaxes: number = Math.abs(total - totalWithoutTaxes);
    const totalWithTaxes: number = total + (gross_value ? 0 : totalOfTaxes);

    return {
        before_taxes: round(totalWithoutTaxes).toFixed(2),
        taxes: round(totalOfTaxes).toFixed(2),
        total: round(total).toFixed(2),
        credits_total: round(totalOfCredits).toFixed(2),
        total_with_credits: round(totalWithTaxes).toFixed(2)
    };
};

const filenameFromDisposition = (header: string | null): string | null => {
    const match = header?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);

    if (!match) return null;

    try {
        return decodeURIComponent(match[1]);
    } catch {
        return match[1];
    }
};

/**
 * Downloads a CSV export with the bearer token, which a plain link cannot send. The server's
 * file name wins when the browser may read Content-Disposition, `filename` is the fallback.
 */
export const downloadCsv = async (url: string, filename: string): Promise<boolean> => {
    const token = getActiveToken();
    const response = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} }).catch(() => null);

    if (!response?.ok) return false;

    const blob = await response.blob().catch(() => null);
    if (!blob) return false;

    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filenameFromDisposition(response.headers.get('Content-Disposition')) ?? filename;
    a.click();
    // Firefox and Safari drop the download when the URL is revoked in the same tick.
    setTimeout(() => URL.revokeObjectURL(a.href), 0);

    return true;
};
